import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Table,
} from "reactstrap";
import axios from "axios";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import moment from "moment";

function DashboardSoporte() {
  const { user } = useContext(AuthContext);
  
  const URL_SUPPORT_TICKETS = process.env.REACT_APP_URL_SUPPORT_TICKETS;
  const URL_SUPPORT_PROJECTS = process.env.REACT_APP_URL_SUPPORT_PROJECTS;

  const [stats, setStats] = useState(null);
  const [proyectos, setProyectos] = useState([]);
  const [ticketsPorProyecto, setTicketsPorProyecto] = useState({});
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  useEffect(() => {
    getStats();
    getProyectos();
  }, []);

  const getStats = () => {
    showLoader();
    
    // Obtener tickets del último mes
    const desde = moment().subtract(30, 'days').format('YYYY-MM-DD');
    
    axios
      .get(`${URL_SUPPORT_TICKETS}Stats?desde=${desde}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setStats(response.data);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  };

  const getProyectos = () => {
    axios
      .get(URL_SUPPORT_PROJECTS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setProyectos(response.data);
        // Obtener tickets por proyecto
        response.data.forEach(proyecto => {
          getTicketsPorProyecto(proyecto._id);
        });
      })
      .catch((err) => console.log(err));
  };

  const getTicketsPorProyecto = (projectId) => {
    const desde = moment().subtract(30, 'days').format('YYYY-MM-DD');
    
    axios
      .get(`${URL_SUPPORT_TICKETS}Stats?projectId=${projectId}&desde=${desde}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setTicketsPorProyecto(prev => ({
          ...prev,
          [projectId]: response.data
        }));
      })
      .catch((err) => console.log(err));
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds) return "N/A";
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const calculatePercentage = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  };

  if (!stats) {
    return (
      <>
        <Header />
        {loader}
      </>
    );
  }

  return (
    <>
      <Header />
      {loader}
      {user.menu_soporte ? (
        <div className="container-fluid">
          <br />
          
          {/* Header */}
          <Row className="mb-4">
            <Col md={12}>
              <h3>Dashboard de Soporte</h3>
              <p className="text-muted">Últimos 30 días</p>
            </Col>
          </Row>

          {/* KPIs */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h2 className="text-primary">{stats.totalTickets}</h2>
                  <p className="text-muted mb-0">Total Tickets</p>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h2 className="text-success">{stats.resolvedByAI}</h2>
                  <p className="text-muted mb-0">
                    Resueltos por IA
                    <br />
                    <Badge color="success">
                      {calculatePercentage(stats.resolvedByAI, stats.totalTickets)}%
                    </Badge>
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h2 className="text-info">{stats.resolvedByTeam}</h2>
                  <p className="text-muted mb-0">
                    Resueltos por Equipo
                    <br />
                    <Badge color="info">
                      {calculatePercentage(stats.resolvedByTeam, stats.totalTickets)}%
                    </Badge>
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h2 className="text-warning">
                    {formatDuration(stats.avgResolutionTimeMs)}
                  </h2>
                  <p className="text-muted mb-0">Tiempo Promedio</p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Gráficas - Distribución por Categoría y Prioridad */}
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <CardHeader>
                  <h5>Tickets por Categoría</h5>
                </CardHeader>
                <CardBody>
                  {stats.byCategory && stats.byCategory.length > 0 ? (
                    <Table size="sm" striped>
                      <thead>
                        <tr>
                          <th>Categoría</th>
                          <th>Cantidad</th>
                          <th>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.byCategory.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <Badge color={
                                item._id === 'bug' ? 'danger' :
                                item._id === 'feature' ? 'success' :
                                item._id === 'question' ? 'info' :
                                item._id === 'data_fix' ? 'warning' :
                                item._id === 'urgent' ? 'danger' : 'secondary'
                              }>
                                {item._id || 'Sin categoría'}
                              </Badge>
                            </td>
                            <td>{item.count}</td>
                            <td>
                              <Badge color="light">
                                {calculatePercentage(item.count, stats.totalTickets)}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-muted">No hay datos disponibles</p>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <CardHeader>
                  <h5>Tickets por Prioridad</h5>
                </CardHeader>
                <CardBody>
                  {stats.byPriority && stats.byPriority.length > 0 ? (
                    <Table size="sm" striped>
                      <thead>
                        <tr>
                          <th>Prioridad</th>
                          <th>Cantidad</th>
                          <th>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.byPriority.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <Badge color={
                                item._id === 'critical' ? 'danger' :
                                item._id === 'high' ? 'warning' :
                                item._id === 'medium' ? 'info' : 'secondary'
                              }>
                                {item._id || 'Sin prioridad'}
                              </Badge>
                            </td>
                            <td>{item.count}</td>
                            <td>
                              <Badge color="light">
                                {calculatePercentage(item.count, stats.totalTickets)}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-muted">No hay datos disponibles</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Tabla resumen por cliente */}
          <Row>
            <Col md={12}>
              <Card>
                <CardHeader>
                  <h5>Resumen por Cliente</h5>
                </CardHeader>
                <CardBody>
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Total Tickets</th>
                        <th>Resueltos IA</th>
                        <th>Resueltos Equipo</th>
                        <th>% IA</th>
                        <th>Tiempo Promedio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proyectos.map((proyecto) => {
                        const projectStats = ticketsPorProyecto[proyecto._id];
                        if (!projectStats) return null;
                        
                        const total = projectStats.totalTickets || 0;
                        const aiResolved = projectStats.resolvedByAI || 0;
                        const teamResolved = projectStats.resolvedByTeam || 0;
                        const avgTime = projectStats.avgResolutionTimeMs || 0;
                        
                        if (total === 0) return null;
                        
                        return (
                          <tr key={proyecto._id}>
                            <td>
                              <strong>{proyecto.clientName}</strong>
                              <br />
                              <small className="text-muted">{proyecto.slug}</small>
                            </td>
                            <td>
                              <Badge color="primary">{total}</Badge>
                            </td>
                            <td>
                              <Badge color="success">{aiResolved}</Badge>
                            </td>
                            <td>
                              <Badge color="info">{teamResolved}</Badge>
                            </td>
                            <td>
                              <Badge color={
                                calculatePercentage(aiResolved, total) > 70 ? 'success' :
                                calculatePercentage(aiResolved, total) > 40 ? 'warning' : 'danger'
                              }>
                                {calculatePercentage(aiResolved, total)}%
                              </Badge>
                            </td>
                            <td>{formatDuration(avgTime)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Estadísticas adicionales */}
          <Row className="mt-4">
            <Col md={4}>
              <Card>
                <CardHeader>
                  <h6>Eficiencia de IA</h6>
                </CardHeader>
                <CardBody className="text-center">
                  <h3 className="text-success">
                    {calculatePercentage(stats.resolvedByAI, stats.totalTickets)}%
                  </h3>
                  <p className="text-muted mb-0">
                    de tickets resueltos sin intervención humana
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardHeader>
                  <h6>Tickets Activos</h6>
                </CardHeader>
                <CardBody className="text-center">
                  <h3 className="text-warning">
                    {stats.totalTickets - stats.resolvedByAI - stats.resolvedByTeam}
                  </h3>
                  <p className="text-muted mb-0">
                    tickets aún por resolver
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardHeader>
                  <h6>Tasa de Resolución</h6>
                </CardHeader>
                <CardBody className="text-center">
                  <h3 className="text-info">
                    {calculatePercentage(
                      stats.resolvedByAI + stats.resolvedByTeam,
                      stats.totalTickets
                    )}%
                  </h3>
                  <p className="text-muted mb-0">
                    de tickets resueltos
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>

        </div>
      ) : undefined}
    </>
  );
}

export default DashboardSoporte;
